import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';

const LoginModal = ({ isOpen, onClose, onSwitchToRegister, pendingCartItem = null, returnPath = '/' }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  const { email, password } = formData;

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const result = await login(email, password);
      
      if (result.success) {
        // Get user data from localStorage to check role
        const userData = JSON.parse(localStorage.getItem('user'));
        
        // Prevent admin users from logging in through modal
        if (userData?.role === 'admin') {
          setError('Admin users must log in through the Admin Login page.');
          // Logout admin user
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          return;
        }
        
        // If there's a pending cart item, add it to cart
        if (pendingCartItem) {
          const cartItems = JSON.parse(localStorage.getItem('cartItems')) || [];
          const existingItemIndex = cartItems.findIndex(cartItem => 
            cartItem.id === pendingCartItem.id && cartItem.frameOption === pendingCartItem.frameOption
          );
          
          if (existingItemIndex !== -1) {
            cartItems[existingItemIndex].quantity += pendingCartItem.quantity;
            cartItems[existingItemIndex].totalPrice = 
              cartItems[existingItemIndex].price * cartItems[existingItemIndex].quantity;
          } else {
            cartItems.push(pendingCartItem);
          }
          
          localStorage.setItem('cartItems', JSON.stringify(cartItems));
          window.dispatchEvent(new Event('cartUpdated'));
        }
        
        // Close modal and refresh page or navigate
        onClose(true);
      } else {
        setError(result.error || 'Login failed. Please try again.');
      }
    } catch (err) {
      console.error('Login failed:', err);
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center overflow-y-auto p-4 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="login-modal-title">
      {/* Backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-[#11110e]/75 backdrop-blur-sm transition-opacity"
        onClick={() => onClose(false)}
        aria-label="Close login"
      />

      {/* Modal */}
      <div className="relative z-10 max-h-[calc(100vh-2rem)] w-full max-w-md overflow-y-auto border border-border bg-surface text-left shadow-[0_30px_90px_rgba(0,0,0,.4)] sm:max-h-[calc(100vh-3rem)]">
          {/* Close button */}
          <button
            onClick={() => onClose(false)}
            className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-full border border-border text-text-secondary transition hover:border-primary hover:text-primary"
            aria-label="Close login"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          <div className="bg-surface px-6 pb-7 pt-8 sm:p-9">
            <div className="mb-7 pr-12">
              <p className="editorial-eyebrow mb-2">Tafsir Art</p>
              <h3 id="login-modal-title" className="font-display text-4xl font-semibold text-text-primary">Welcome back</h3>
              <p className="mt-2 text-sm text-text-secondary">Sign in to continue to your account and collection.</p>
            </div>

            {error && (
              <div className="mb-5 border border-red-400 bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-200">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="email" className="mb-2 block text-sm font-medium text-text-primary">
                  Email
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={email}
                  onChange={handleChange}
                  className="field"
                  required
                  autoFocus
                  placeholder="your@email.com"
                />
              </div>

              <div className="mb-6">
                <label htmlFor="password" className="mb-2 block text-sm font-medium text-text-primary">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={password}
                  onChange={handleChange}
                  className="field"
                  required
                  placeholder="••••••••"
                />
              </div>

              <button
                type="submit"
                className="btn-primary mb-5 w-full"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Signing in...
                  </span>
                ) : (
                  'Sign In'
                )}
              </button>

              <div className="text-center text-sm text-text-secondary">
                Don't have an account?{' '}
                <button
                  type="button"
                  onClick={onSwitchToRegister}
                  className="text-primary hover:text-primary-dark font-medium"
                >
                  Create one
                </button>
              </div>
            </form>
          </div>
      </div>
    </div>,
    document.body
  );
};

export default LoginModal;
